import React from 'react'
import { useAtSize, SocialBar, HeaderBlock, SocialMenu, Providers } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Person } from '../../types/persons'

const mediaBannerCss = css`
  display: block;
  position: relative;
  text-align: center;
  margin: auto;
`

const socialBarCss = css`
  position: relative;
  width: 400px;
  margin-left: 50%;
  left: -197px;

  & button {
    border: 1px solid #fff;
    border-radius: 5px;
    margin-right: 15px;
    color: #fff;
  }
`

const headerCss = css`
  display: block;
  position: relative;
  margin-bottom: 14px;

  & h5 {
    font-weight: none;
  }
`

const shareCss = css`
  position: relative;
  padding: 8px;
  margin: 8px;
  margin-right: 24px;

  & button,
  button:hover {
    color: white;
  }
`

const apps = ['facebook', 'twitter', 'whatsApp']

type ShareButtonProps = {
  name: string
  product: string
  analytics: Record<string, unknown>
}

export function ShareButton({ name, product, analytics }: ShareButtonProps): JSX.Element {
  const [t] = useTranslation()

  const sharable = {
    url: window.location.href,
    subject: t('person-page-banner.share-subject', 'See the exciting information I found for {name}', { name }),
  }

  return (
    <Providers>
      <div css={shareCss}>
        <SocialMenu
          sharable={sharable}
          apps={apps}
          product={product}
          analytics={analytics}
          inline={false}
          buttonEmphasis="low"
        />
      </div>
    </Providers>
  )
}

export default function AncestorSocial({
  person,
  analytics,
}: {
  person: Person
  analytics: Record<string, string>
}): JSX.Element {
  const [t] = useTranslation()
  const atSize = useAtSize()

  const isMobile = atSize({ lg: false, default: true })
  const { name } = person
  const sharable = {
    url: window.location.href,
    subject: t('ancestors.social-media-banner.share-subject', 'See the exciting information I found for {name}', {
      name,
    }),
  }
  const product =
    `${analytics.link_app ? `${analytics.link_app} ` : ''}${analytics.link_page || ''}`.replace(/: /g, '_') ||
    'Person_Footer' // Must not have colons or spaces

  return (
    <div css={mediaBannerCss}>
      <div css={headerCss}>
        <HeaderBlock
          centered
          size="sm"
          heading={t('ancestors.social-media-banner.header', 'Share this with your family and friends.')}
        />
      </div>
      {!isMobile && (
        <div css={socialBarCss}>
          <SocialBar sharable={sharable} apps={apps} product={product} analytics={analytics} />
        </div>
      )}
      {isMobile && (
        <ShareButton name={name} product={product} analytics={analytics} data-testid="social-media-share-button" />
      )}
    </div>
  )
}
